import { Component } from '@nicolas-riciotti/light2'

/**
 * <newsletter-overlay> — full-screen subscribe overlay.
 *
 * Markup: template-parts/newsletter-overlay.php (the form inside is a <newsletter-form>).
 *
 * - Any same-origin link to the /subscribe page opens the overlay instead of navigating.
 * - On the /subscribe page it is the page itself (data-page, page-subscribe.php): no link
 *   interception or scroll lock, and closing it goes back to the previous page on this site, or home.
 * - Esc and the X close it; Tab stays inside while it is open; the page behind does not scroll.
 */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'

const normalizePath = path => path.replace(/\/?$/, '/')

export default class NewsletterOverlay extends Component {

    created() {
        this.isOpen = false
        this.onKeydown = this.onKeydown.bind(this)
        this.onDocumentClick = this.onDocumentClick.bind(this)
    }

    attached() {
        this.isPage = this.hasAttribute('data-page')
        this.path = normalizePath(new URL(this.dataset.url || '/subscribe/', window.location.href).pathname)

        this.querySelectorAll('[newsletter-overlay-close]').forEach($el => {
            $el.addEventListener('click', () => this.close())
        })
        if (this.isPage) return

        // Capture phase: <web-application> handles every internal link click itself (stopPropagation + navigate).
        document.addEventListener('click', this.onDocumentClick, true)
    }

    onDocumentClick(e) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
        const $link = e.target.closest?.('a[href]')
        if (!$link || $link.target === '_blank' || this.contains($link)) return

        let url
        try { url = new URL($link.href, window.location.href) } catch { return }
        if (url.origin !== window.location.origin || normalizePath(url.pathname) !== this.path) return

        e.preventDefault()
        e.stopPropagation()
        this.open()
    }

    open() {
        if (this.isOpen) return
        this.isOpen = true

        const header = document.querySelector('app-header')
        if (header?.isMenuOpened && typeof header.closeMenu === 'function') header.closeMenu()

        this.$previousFocus = document.activeElement
        this.hidden = false
        document.documentElement.style.overflow = 'hidden'
        document.addEventListener('keydown', this.onKeydown)
        requestAnimationFrame(() => {
            this.classList.add('is-open')
            this.querySelector('input[name="email"]')?.focus({ preventScroll: true })
        })
        window.clarity?.('set', 'newsletter', 'overlay-open')
    }

    close() {
        if (this.isPage) {
            const fromSite = document.referrer && new URL(document.referrer).origin === window.location.origin
            if (fromSite && window.history.length > 1) window.history.back()
            else window.location.href = this.dataset.home || '/'
            return
        }
        if (!this.isOpen) return
        this.isOpen = false

        this.classList.remove('is-open')
        document.documentElement.style.overflow = ''
        document.removeEventListener('keydown', this.onKeydown)
        clearTimeout(this.hideTimer)
        this.hideTimer = setTimeout(() => { if (!this.isOpen) this.hidden = true }, 400)
        this.$previousFocus?.focus?.({ preventScroll: true })
    }

    onKeydown(e) {
        if (e.key === 'Escape') {
            e.preventDefault()
            this.close()
            return
        }
        if (e.key !== 'Tab') return

        const items = Array.from(this.querySelectorAll(FOCUSABLE)).filter($el => $el.offsetParent !== null || $el.tagName === 'IFRAME')
        if (!items.length) return
        const first = items[0]
        const last = items[items.length - 1]
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault()
            last.focus()
        }
        else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault()
            first.focus()
        }
        else if (!this.contains(document.activeElement)) {
            e.preventDefault()
            first.focus()
        }
    }
}

customElements.define('newsletter-overlay', NewsletterOverlay)
